import { useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useModulePermissions } from "../../contexts/ModulePermissionsContext";
import { ModuleConfig } from "../../types/ModuleConfig";
import { Client } from "../../generated/graphql";

import prospectingModuleConfig from "../prospecting/moduleConfig";
import employeesModuleConfig from "../employees/moduleConfig";
import clientsModuleConfig from "../clients/moduleConfig";
import companiesModuleConfig from "../companies/moduleConfig";
import shiftsModuleConfig from "../shifts/moduleConfig";
import emailsModuleConfig from "../emails/moduleConfig";
import { calendarsModuleConfig } from "../calendars/moduleConfig";

/** Modules the Home dashboard links to, keyed by moduleConfig.id. */
const HOME_MODULES: Record<string, Pick<ModuleConfig, "id" | "permissions">> = {
  prospecting: prospectingModuleConfig,
  employees: employeesModuleConfig,
  clients: clientsModuleConfig,
  companies: companiesModuleConfig,
  shifts: shiftsModuleConfig,
  emails: emailsModuleConfig,
  calendars: calendarsModuleConfig,
};

type ViewerPerms = Pick<Client, "permissions"> | null;

/**
 * Same gate FloatingNavbar applies: ADMIN / TENANT_MASTER_ADMIN see every
 * module; everyone else needs one of the module's `view` permissions, taken
 * from the backend permission map when it has resolved, else the config's own
 * hardcoded list. Unknown (empty) → hidden, never disabled.
 */
export const useModuleAccess = () => {
  const { user } = useAuth();
  const { permissions: backendPerms, loading } = useModulePermissions();
  const viewerPerms = useMemo<string[]>(() => (user as ViewerPerms)?.permissions ?? [], [user]);
  const isSuperAdmin = viewerPerms.includes("ADMIN") || viewerPerms.includes("TENANT_MASTER_ADMIN");

  const canView = useCallback(
    (moduleId: string): boolean => {
      if (isSuperAdmin) return true;
      const config = HOME_MODULES[moduleId];
      if (!config) return false;
      const fromBackend = backendPerms[moduleId]?.view;
      const view = fromBackend && fromBackend.length > 0 ? fromBackend : config.permissions.view;
      if (!view || view.length === 0) return false;
      return view.some((p) => viewerPerms.includes(p));
    },
    [isSuperAdmin, backendPerms, viewerPerms],
  );

  return { canView, loading, isSuperAdmin };
};
